import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { KhenThuongAPI } from '../../services/feature-api';
import API from '../../services/API'; // uploadImage nằm ở default export API (đi /system/file NoAuth), KHÔNG ở CungVeDichAPI
import DateSelect from '../DateSelect';
import { PageShell, Icon, NutXemQuyDinh, NhanMotTrongHai, Hoac, YeuCauTuyenSi, PHUONG_TIEN, fmtTien, fmtNgay } from './khen-thuong-shared';
import { TheChucMung } from './the-chuc-mung';

// /khenthuong/du-lich-2026/dang-ky — form Đăng ký + cam kết doanh số Du lịch 2026.
// ĐK1 (chặn nộp): dsCamKet ≥ 100.000.000 (bằng vẫn đạt — 15/09/2026 chốt 200tr, 02/11/2026 hạ còn 100tr) và ≥ dsHienTai. dsHienTai = tổng DS 2026 các tháng đã duyệt (màu xanh ở /doanh-so) — BE tính.
// ĐK2 (chỉ thông báo): dsHienTai > 1,5 × DS duyệt của F1 cao nhất (bằng 1,5 = chưa đạt; BE tính datDk2). ĐK3: CCCD + SĐT + phương tiện → BE ghi vào users + dl_2026_detail.

const chiSo = (s) => String(s || '').replace(/\D/g, '');
const fmtNhap = (digits) => (digits ? Number(digits).toLocaleString('vi-VN') : '');

const TieuDe = ({ so, text, sub }) => (
  <div className="flex items-start gap-2 mb-2">
    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-red-600 text-white text-[13px] font-black flex items-center justify-center">{so}</span>
    <div>
      <p className="text-[15px] font-black text-gray-900 leading-tight">{text}</p>
      {sub && <p className="text-[12px] text-gray-500">{sub}</p>}
    </div>
  </div>
);

const O = ({ label, children }) => (
  <label className="block">
    <span className="block text-[12px] font-bold text-gray-600 mb-1">{label}</span>
    {children}
  </label>
);
const inputCls = 'w-full px-3 py-3 rounded-xl border border-gray-200 bg-gray-50 text-[16px] focus:ring-2 focus:ring-red-500 outline-none';

const DuLich2026Form = ({ accessToken, userInfo, onLogout, totalPoints, onRefreshPoints }) => {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [dk, setDk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [dsCamKet, setDsCamKet] = useState('');   // chuỗi chữ số
  const [chamDs, setChamDs] = useState(false);
  const [hoTen, setHoTen] = useState('');
  const [soCccd, setSoCccd] = useState('');
  const [ngaySinh, setNgaySinh] = useState('');
  const [sdt, setSdt] = useState('');
  const [anhCccd, setAnhCccd] = useState('');
  const [uploading, setUploading] = useState(false);
  const [phuongTien, setPhuongTien] = useState('');
  const [daDoc, setDaDoc] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [ketQua, setKetQua] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true); setLoadError('');
    const r = await KhenThuongAPI.getDieuKien(accessToken);
    if (r.success && r.data) {
      setDk(r.data);
      const p = r.data.profile || {};
      setHoTen(p.hoTenCccd || '');
      setSoCccd(chiSo(p.soCccd));
      setNgaySinh(p.ngaySinh || '');
      setSdt(chiSo(p.sdtLienHe || userInfo?.phoneNumber));
      setAnhCccd(p.anhCccd || '');
    } else setLoadError(r.error || 'Không tải được điều kiện đăng ký');
    setLoading(false);
  }, [accessToken, userInfo?.phoneNumber]);

  useEffect(() => { load(); }, [load]);

  // ---- ĐK1 ----
  const dsToiThieu = dk?.dsToiThieu ?? 100000000;
  const dsHienTai = Number(dk?.dsHienTai) || 0;
  const dsNum = Number(dsCamKet) || 0;
  let loiDs = '';
  if (!dsCamKet) loiDs = 'Bạn chưa nhập doanh số cam kết';
  else if (dsNum < dsToiThieu) loiDs = `Doanh số cam kết phải từ ${fmtTien(dsToiThieu)} trở lên`;
  else if (dsNum < dsHienTai) loiDs = `Doanh số cam kết phải ≥ doanh số hiện tại đã duyệt ${fmtTien(dsHienTai)}`;
  const dsHopLe = !loiDs;

  // ---- ĐK2 ----
  // F1 có trong DB nhưng DS duyệt 2026 = 0 → BE trả tyLe null, coi như đạt; FE hiển thị như chưa có F1 (kèm tên)
  const f1 = dk?.f1CaoNhat && Number(dk.f1CaoNhat.ds) > 0 ? dk.f1CaoNhat : null;
  const f1KhongDs = dk?.f1CaoNhat && !(Number(dk.f1CaoNhat.ds) > 0) ? dk.f1CaoNhat : null;
  const tyLe = dk?.tyLe;
  const nguongF1 = dk?.nguongF1 ?? 1.5;
  const datDk2 = dk?.datDk2;

  // ---- ĐK3 ----
  const loiDk3 = [];
  if (!anhCccd) loiDk3.push('ảnh CCCD');
  if (!hoTen.trim()) loiDk3.push('họ tên');
  if (soCccd.length < 9 || soCccd.length > 12) loiDk3.push('số CCCD (9–12 số)');
  if (!ngaySinh) loiDk3.push('ngày sinh');
  if (sdt.length < 9) loiDk3.push('SĐT liên hệ');
  if (!phuongTien) loiDk3.push('phương tiện');

  const guiDuoc = dsHopLe && loiDk3.length === 0 && daDoc && !uploading && !submitting;

  const chonAnh = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setSubmitError('');
    try {
      const r = await API.uploadImage(file);
      setAnhCccd(r.fileUrl);
    } catch (err) {
      setSubmitError(err.message || 'Không tải được ảnh');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const nop = async () => {
    setChamDs(true);
    if (!guiDuoc) return;
    setSubmitting(true); setSubmitError('');
    const r = await KhenThuongAPI.dangKy(accessToken, {
      dsCamKet: dsNum, hoTenCccd: hoTen.trim(), soCccd, ngaySinh, sdtLienHe: sdt, anhCccd, phuongTien, daDocLuuY: true,
    });
    if (r.success) { setKetQua(r.data); window.scrollTo(0, 0); }
    else setSubmitError(r.error || 'Đăng ký không thành công, vui lòng thử lại');
    setSubmitting(false);
  };

  if (ketQua) return <TheChucMung user={ketQua.user} hoSo={ketQua.hoSo} loiGuiGam={dk?.chienDich?.loiGuiGam} />;

  const chienDich = dk?.chienDich;
  const dong = chienDich && chienDich.tinhTrang !== 'mo';
  const daNop = !!dk?.hoSo;
  const namNay = new Date().getFullYear();

  return (
    <PageShell userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} onRefreshPoints={onRefreshPoints} title="Đăng ký Du lịch 2026">
      <div className="px-3 pt-3 space-y-3">
        {loading && <p className="text-center text-gray-400 text-sm py-8">Đang tải điều kiện...</p>}
        {loadError && <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-[13px] px-3 py-2">{loadError}</div>}

        {dk && daNop && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-300 px-4 py-4 text-center">
            <Icon name="verified" size={40} className="text-emerald-600" />
            <p className="mt-1 text-[16px] font-black text-emerald-800">Bạn đã đăng ký rồi</p>
            <p className="text-[13px] text-emerald-700">Mã đăng ký <b>{dk.hoSo.maDangKy}</b> · cam kết {fmtTien(dk.hoSo.dsCamKet)}</p>
            <button type="button" onClick={() => navigate('/khenthuong/du-lich-2026/tinh-trang')}
                    className="mt-3 w-full rounded-xl bg-emerald-600 text-white py-3 text-[14px] font-bold">Theo dõi tình trạng và kết quả</button>
          </div>
        )}
        {dk && !daNop && dong && (
          <div className="rounded-2xl bg-gray-100 border border-gray-300 px-4 py-4 text-center">
            <Icon name="lock" size={36} className="text-gray-500" />
            <p className="mt-1 text-[15px] font-black text-gray-700">Chiến dịch đã đóng đăng ký</p>
            <p className="text-[12px] text-gray-500">Hạn đăng ký {fmtNgay(chienDich.ketThucDk)}</p>
          </div>
        )}

        {dk && !daNop && !dong && (
          <>
            {/* ---------- ĐK1 ---------- */}
            <div className="rounded-2xl bg-white border-2 border-red-200 shadow-sm px-4 py-4">
              <TieuDe so="1" text="Cam kết doanh số năm 2026" sub={`Từ ${fmtTien(dsToiThieu)} trở lên và không thấp hơn doanh số hiện tại`} />
              <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 flex items-center justify-between mb-3">
                <span className="text-[12px] font-semibold text-gray-600">Doanh số hiện tại đã duyệt (1)</span>
                <span className="text-[15px] font-black text-emerald-700">{fmtTien(dsHienTai)}</span>
              </div>
              <div className={`rounded-2xl border-2 px-3 py-3 ${chamDs && !dsHopLe ? 'border-red-500 bg-red-50' : dsHopLe ? 'border-emerald-500 bg-emerald-50' : 'border-red-300 bg-white'}`}>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Doanh số cam kết 2026</p>
                <div className="flex items-end gap-2">
                  <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0"
                         value={fmtNhap(dsCamKet)}
                         onChange={(e) => setDsCamKet(chiSo(e.target.value).slice(0, 13))}
                         onBlur={() => setChamDs(true)}
                         className="flex-1 min-w-0 bg-transparent outline-none text-[32px] font-black text-gray-900 placeholder-gray-300 tracking-tight" />
                  <span className="text-[18px] font-black text-gray-500 pb-1.5">đ</span>
                </div>
                {dsCamKet && <p className="text-[12px] text-gray-500 font-semibold">≈ {(dsNum / 1e6).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} triệu đồng</p>}
              </div>
              {chamDs && !dsHopLe && (
                <p className="mt-2 flex items-start gap-1 text-[13px] font-bold text-red-600"><Icon name="error" size={18} />{loiDs}</p>)}
              {dsHopLe && (
                <p className="mt-2 flex items-center gap-1 text-[13px] font-bold text-emerald-700"><Icon name="check_circle" size={18} />Doanh số cam kết hợp lệ</p>)}
            </div>

            {/* ---------- Đạt 1 trong 2: yêu cầu doanh số (2) HOẶC yêu cầu tuyển sỉ (3) — anh Bình chốt 28/09/2026 ---------- */}
            <NhanMotTrongHai />
            <div className="rounded-2xl bg-white border border-gray-200 shadow-sm px-4 py-4">
              <TieuDe so="2" text="Yêu cầu doanh số" sub={`Doanh số duyệt 2026 từ ${fmtTien(dsToiThieu)} và gấp hơn ${nguongF1.toLocaleString('vi-VN')} lần F1 có doanh số duyệt cao nhất`} />
              {f1 ? (
                <>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] text-gray-600">F1 cao nhất</span>
                      <span className="text-[13px] font-black text-gray-900">{f1.codeFacebook}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] text-gray-600">Doanh số duyệt của F1</span>
                      <span className="text-[13px] font-bold text-gray-900">{fmtTien(f1.ds)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] text-gray-600">Doanh số của bạn</span>
                      <span className="text-[13px] font-bold text-gray-900">{fmtTien(dsHienTai)}</span>
                    </div>
                  </div>
                  <div className={`mt-2 rounded-xl px-3 py-2.5 flex items-center gap-2 ${datDk2 ? 'bg-emerald-50 border border-emerald-300' : 'bg-amber-50 border border-amber-300'}`}>
                    <Icon name={datDk2 ? 'check_circle' : 'warning'} size={26} className={datDk2 ? 'text-emerald-600' : 'text-amber-600'} />
                    <div>
                      <p className={`text-[14px] font-black ${datDk2 ? 'text-emerald-800' : 'text-amber-800'}`}>
                        {datDk2 ? 'Đã vượt' : 'Chưa vượt'} {nguongF1.toLocaleString('vi-VN')} lần F1 cao nhất
                      </p>
                      <p className={`text-[12px] font-semibold ${datDk2 ? 'text-emerald-700' : 'text-amber-700'}`}>
                        Hiện tại bạn gấp <b className="text-[15px]">{tyLe == null ? '—' : tyLe.toLocaleString('vi-VN')}</b> lần {f1.codeFacebook}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="rounded-xl bg-emerald-50 border border-emerald-300 px-3 py-2.5 flex items-center gap-2">
                  <Icon name="check_circle" size={26} className="text-emerald-600" />
                  <p className="text-[13px] font-semibold text-emerald-800">{f1KhongDs ? `F1 cao nhất ${f1KhongDs.codeFacebook} chưa có doanh số duyệt năm 2026` : 'Chưa có F1 nào có doanh số duyệt năm 2026'} → điều kiện này hiện đạt.</p>
                </div>
              )}
              <p className="mt-2 text-[11px] text-gray-400">Điều kiện này được xét lại khi khoá doanh số 15/11/2026. Bạn vẫn đăng ký được ngay bây giờ.</p>
            </div>
            <Hoac />
            <YeuCauTuyenSi tuyenSi={dk.tuyenSi} so="3" />

            {/* ---------- ĐK3 ---------- */}
            <div className="rounded-2xl bg-white border border-gray-200 shadow-sm px-4 py-4 space-y-3">
              <TieuDe so="4" text="Thông tin cá nhân đi du lịch" sub="Đúng theo Căn cước công dân để đặt vé" />

              <div>
                <span className="block text-[12px] font-bold text-gray-600 mb-1">Ảnh mặt trước CCCD</span>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={chonAnh} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                        className={`w-full rounded-xl border-2 border-dashed overflow-hidden ${anhCccd ? 'border-emerald-400' : 'border-gray-300 bg-gray-50'}`}>
                  {anhCccd
                    ? <img src={anhCccd} alt="CCCD" className="w-full max-h-56 object-contain bg-black/5" />
                    : <div className="py-6 flex flex-col items-center text-gray-500">
                        <Icon name="add_a_photo" size={34} />
                        <span className="text-[13px] font-semibold mt-1">{uploading ? 'Đang tải ảnh...' : 'Chụp / chọn ảnh CCCD'}</span>
                      </div>}
                </button>
                {anhCccd && !uploading && <p className="text-[11px] text-gray-500 mt-1">Bấm vào ảnh để chọn lại.</p>}
              </div>

              <O label="Họ và tên (theo CCCD)">
                <input type="text" value={hoTen} onChange={(e) => setHoTen(e.target.value)} placeholder="NGUYỄN VĂN A" className={inputCls} />
              </O>
              <O label="Số CCCD">
                <input type="text" inputMode="numeric" pattern="[0-9]*" value={soCccd} onChange={(e) => setSoCccd(chiSo(e.target.value).slice(0, 12))}
                       placeholder="12 chữ số" className={inputCls} />
              </O>
              <div>
                <span className="block text-[12px] font-bold text-gray-600 mb-1">Ngày sinh</span>
                <DateSelect value={ngaySinh} onChange={setNgaySinh} yearFrom={1940} yearTo={namNay - 15} />
              </div>
              <O label="Số điện thoại liên hệ">
                <input type="tel" inputMode="numeric" value={sdt} onChange={(e) => setSdt(chiSo(e.target.value).slice(0, 11))} placeholder="09xxxxxxxx" className={inputCls} />
              </O>
              <div>
                <span className="block text-[12px] font-bold text-gray-600 mb-1">Phương tiện di chuyển</span>
                <div className="grid grid-cols-2 gap-2">
                  {PHUONG_TIEN.map((p) => (
                    <button key={p.value} type="button" onClick={() => setPhuongTien(p.value)}
                            className={`rounded-xl py-3 text-[14px] font-bold flex items-center justify-center gap-1.5 border-2 ${
                              phuongTien === p.value ? 'bg-red-600 border-red-600 text-white shadow' : 'bg-white border-gray-200 text-gray-700'}`}>
                      <Icon name={p.icon} size={18} />{p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ---------- Chú ý ---------- */}
            <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 px-4 py-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="flex items-center gap-1 text-[15px] font-black text-amber-900"><Icon name="campaign" size={20} />Chú ý</p>
                <NutXemQuyDinh fileQuyDinh={chienDich?.fileQuyDinh} />
              </div>
              <ul className="space-y-2 text-[13px] text-amber-900 leading-relaxed list-disc pl-4">
                <li>Bạn cần đọc và hiểu rõ <b>4 tiêu chí bắt buộc</b> đạt đủ trong Quy định về xét thưởng Du lịch năm 2026 (bấm <b>Xem quy định</b> ở góc phải).</li>
                <li>Từ <b>15/11/2026 – 30/11/2026</b>: hệ thống khoá dữ liệu Doanh số trên App, bắt đầu xét duyệt cả 4 tiêu chí và gửi kết quả về mỗi người qua App.</li>
                <li>Chú ý chốt đơn nhập hàng với Tuyến trên và cập nhật đầy đủ DS cả năm 2026 (<b>T1→T12</b>) lên App <b>trước ngày 15/11/2026</b>.</li>
              </ul>
              <label className={`mt-3 flex items-start gap-2 rounded-xl px-3 py-3 border-2 cursor-pointer ${daDoc ? 'bg-white border-emerald-500' : 'bg-white border-amber-300'}`}>
                <input type="checkbox" checked={daDoc} onChange={(e) => setDaDoc(e.target.checked)} className="mt-0.5 w-5 h-5 accent-red-600" />
                <span className="text-[14px] font-bold text-gray-800">Tôi đã đọc và hiểu rõ tất cả các lưu ý trên.</span>
              </label>
            </div>

            {submitError && <div className="rounded-xl bg-red-50 border border-red-300 text-red-700 text-[13px] font-semibold px-3 py-2">{submitError}</div>}
            {chamDs && !guiDuoc && !submitting && (
              <p className="text-[12px] text-gray-500 text-center">
                {!dsHopLe ? 'Kiểm tra lại doanh số cam kết.' : loiDk3.length ? `Còn thiếu: ${loiDk3.join(', ')}.` : !daDoc ? 'Hãy tick "Tôi đã đọc và hiểu rõ".' : ''}
              </p>)}
            <button type="button" onClick={nop} disabled={submitting}
                    className={`w-full rounded-2xl py-4 text-[17px] font-black shadow-lg flex items-center justify-center gap-2 ${
                      guiDuoc ? 'bg-red-600 text-white active:bg-red-700' : 'bg-gray-300 text-gray-600'}`}>
              <Icon name="how_to_reg" size={22} />{submitting ? 'Đang gửi...' : 'Xác nhận đăng ký'}
            </button>
          </>
        )}
      </div>
    </PageShell>
  );
};

export default DuLich2026Form;
